import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hc } from 'hono/client';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';

interface StaticPage {
  id: string;
  code: string;
  title: string;
  content: string;
  locale?: string;
  createdAt: string;
  updatedAt: string;
}

// Partial type definition for Hono generic
type StaticPageClient = {
  api: {
    content: {
      'static-pages': {
        $get: () => Promise<Response>;
        $post: (args: { json: Partial<StaticPage> }) => Promise<Response>;
      };
    };
  };
};
const client = hc('/') as unknown as StaticPageClient;

const formSchema = z.object({
  title: z.string().min(1, 'タイトルは必須です'),
  content: z.string().min(1, '本文は必須です'),
  locale: z.string(),
});

const PAGE_LABELS: Record<string, string> = {
  terms: '利用規約',
  privacy: 'プライバシーポリシー',
  law: '特定商取引法に基づく表記',
  policy: '入学者受入方針',
  guide: '募集要項',
  contact: '問い合わせ窓口',
};

export function StaticPageForm() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      content: '',
      locale: 'ja',
    },
  });

  const { data: existingPage, isLoading } = useQuery({
    queryKey: ['static-pages'], // Fetch all and filter client-side since API is list-only for now
    queryFn: async () => {
      const res = await client.api.content['static-pages'].$get();
      return (await res.json()) as StaticPage[];
    },
  });

  useEffect(() => {
    if (existingPage && code) {
      const page = existingPage.find((p: StaticPage) => p.code === code);
      if (page) {
        form.reset({
          title: page.title,
          content: page.content,
          locale: page.locale ?? 'ja',
        });
      } else {
        // Set default title based on code
        form.setValue('title', PAGE_LABELS[code] || '');
      }
    }
  }, [existingPage, code, form]);

  const mutation = useMutation({
    mutationFn: async (values: z.infer<typeof formSchema>) => {
      if (!code) throw new Error('No code specified');
      const res = await client.api.content['static-pages'].$post({
        json: {
          code,
          ...values,
        },
      });
      if (!res.ok) {
        throw new Error('Failed to save');
      }
      return await res.json();
    },
    onSuccess: () => {
      toast({
        title: '保存しました',
        description: '静的コンテンツを更新しました。',
      });
      queryClient.invalidateQueries({ queryKey: ['static-pages'] });
      navigate('/content/static');
    },
    onError: () => {
      toast({
        title: 'エラー',
        description: '保存に失敗しました。',
        variant: 'destructive',
      });
    },
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!code || !PAGE_LABELS[code]) {
    return <div>無効なページ指定です。</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">{PAGE_LABELS[code]} 編集</h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit((data) => mutation.mutate(data))} className="space-y-8">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>タイトル</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem>
                <FormLabel>本文 (Markdown形式)</FormLabel>
                <FormControl>
                  <Textarea className="min-h-[400px] font-mono" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/content/static')}>
              キャンセル
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? '保存中...' : '保存'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
